import { supabase } from "@/integrations/supabase/client";

export interface NoteContextHandoff {
  content: string; type: "idea" | "insight" | "task";
  note_id: string; user_id: string; handoff_id: string;
}
const uuid = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
const pending = new Map<string, { value: NoteContextHandoff; expiresAt: number }>();
let subscribed = false;
let currentOwner: string | null = null;
const lifetimeMs = 10 * 60 * 1000;

/** Only a confirmed, owned send may create a one-use navigation capability.
 * It lives in memory: persisted history state cannot revive it after reload.
 */
export function createNoteContextHandoff(note: { id: string; user_id: string; content: string; type: string }, ownerId: string): NoteContextHandoff | null {
  if (!uuid(ownerId) || note.user_id !== ownerId || !uuid(note.id) || typeof note.content !== "string" || !note.content.trim() || note.content.length > 32_000 || note.content.includes("\0") || !["idea", "insight", "task"].includes(note.type)) return null;
  if (!subscribed) {
    currentOwner = ownerId;
    subscribed = true;
    supabase.auth.onAuthStateChange((_event, session) => {
      const nextOwner = session?.user.id ?? null;
      if (nextOwner !== currentOwner) pending.clear();
      currentOwner = nextOwner;
    });
  }
  if (currentOwner !== ownerId) return null;
  const now = Date.now();
  for (const [id, entry] of pending) if (entry.expiresAt <= now) pending.delete(id);
  while (pending.size >= 20) pending.delete(pending.keys().next().value!);
  const value: NoteContextHandoff = { content: note.content, type: note.type as NoteContextHandoff["type"], note_id: note.id, user_id: ownerId, handoff_id: crypto.randomUUID() };
  pending.set(value.handoff_id, { value: { ...value }, expiresAt: now + lifetimeMs });
  return value;
}

export function takeOwnedNoteContext(value: unknown, ownerId: string | null): NoteContextHandoff | null {
  if (!ownerId || currentOwner !== ownerId || !value || typeof value !== "object" || Array.isArray(value)) return null;
  const input = value as Record<string, unknown>;
  if (!uuid(input.handoff_id)) return null;
  const entry = pending.get(input.handoff_id);
  pending.delete(input.handoff_id);
  if (!entry || entry.expiresAt <= Date.now() || entry.value.user_id !== ownerId || Object.keys(input).length !== 5 ||
    Object.keys(entry.value).some(key => input[key] !== entry.value[key as keyof NoteContextHandoff])) return null;
  return { ...entry.value };
}
